import { useQuery, useMutation, useQueryClient, UseQueryOptions, UseMutationOptions } from '@tanstack/react-query';
import { serverApi, pluginApi, levelingApi, automationApi } from '@/lib/api';

export interface UseApiOptions<T> {
  enabled?: boolean;
  refetchInterval?: number;
  onSuccess?: (data: T) => void;
  onError?: (error: Error) => void;
}

// Generic hook for GET requests
export function useApiQuery<T>(
  queryKey: string[],
  queryFn: () => Promise<T>,
  options?: UseApiOptions<T> & Omit<UseQueryOptions<T>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey,
    queryFn,
    enabled: options?.enabled !== false,
    refetchInterval: options?.refetchInterval,
    onSuccess: options?.onSuccess,
    onError: options?.onError,
    ...options,
  });
}

// Generic hook for mutations (POST, PUT, DELETE)
export function useApiMutation<TData, TVariables = void>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options?: UseMutationOptions<TData, Error, TVariables> & {
    invalidateQueries?: string[][];
  }
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: (data, variables, context) => {
      // Invalidate related queries
      if (options?.invalidateQueries) {
        options.invalidateQueries.forEach(queryKey => {
          queryClient.invalidateQueries({ queryKey });
        });
      }
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
}

// Hook for server data
export function useServers() {
  return useApiQuery(
    ['servers'],
    async () => {
      try {
        const result = await serverApi.getServers();
        return result;
      } catch (error: any) {
        console.error('[useServers] Error fetching servers:', {
          message: error.message,
          status: error.status,
          details: error,
        });
        throw error;
      }
    },
    {
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchIntervalInBackground: false,
      onError: (error: any) => {
        console.error('[useServers] React Query onError:', error);
      },
    }
  );
}

// Hook for single server
export function useServer(serverId: string | null) {
  return useApiQuery(
    ['servers', serverId!],
    () => serverApi.getServer(serverId!),
    { 
      enabled: !!serverId,
      staleTime: 1 * 60 * 1000, // 1 minute
      refetchOnWindowFocus: true,
    }
  );
}

// Hook for plugins
export function usePlugins(serverId: string | null) {
  return useApiQuery(
    ['servers', serverId!, 'plugins'],
    () => pluginApi.getPlugins(serverId!),
    { 
      enabled: !!serverId,
      staleTime: 1 * 60 * 1000, // 1 minute
      refetchOnWindowFocus: true,
    }
  );
}

// Hook for leaderboard
export function useLeaderboard(serverId: string | null, limit = 10, offset = 0) {
  return useApiQuery(
    ['servers', serverId!, 'leaderboard', limit, offset],
    () => levelingApi.getLeaderboard(serverId!, limit, offset),
    { enabled: !!serverId }
  );
}

// Hook for automations
export function useAutomations(serverId: string | null) {
  return useApiQuery(
    ['servers', serverId!, 'automations'],
    () => automationApi.getAutomations(serverId!),
    { enabled: !!serverId }
  );
}
